import { BookOpen, Calendar, Clock2, Link } from "lucide-react";
import { Button, Divider, Drawer } from "antd";
import clsx from "clsx";
import { useState } from "react";
import Input from "antd/es/input/Input";
import ChatBox from "./ChatBox";
import { useSendConversationsMutation } from "../../../hooks/api/Students/Conversations/useSendConversationsMutation";
import { useMarkAsCompletedMutation } from "../../../hooks/api/Students/Bookings/useMarkAsCompletedMutation";
import { MessageOutlined } from "@ant-design/icons";
import Moment from "react-moment";
import AvailabilityBadge from "../Tutors/Components/AvailabilityBadge";

type Props = {
  id: string;
  subject?: any;
  tutorShipType?: any;
  date?: any;
  tutor: any;
  timeSlots?: any;
  status?: "confirmed" | "pending" | "cancelled" | string;
  price?: any;
  duration: any;
  notes?: any;
};

const STATUS_COLORS: Record<string, string> = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  default: "bg-gray-100 text-gray-700",
};

const SessionCard = ({
  _id,
  subject,
  subjectName,
  gradeCategory,
  tutorShipType,
  student,
  tutor,
  date,
  startUtc,
  endUtc,
  timeSlots,
  status,
  price,
  duration,
  notes,
  isPaidOut,
  createdAt,
  updatedAt,
  userLocalStart,
  userLocalEnd,
}: any) => {
  // const isSessionEnded = (date: string, duration: number) => {
  //   const sessionEndTime = new Date(date).getTime() + duration * 60000;
  //   return Date.now() >= sessionEndTime;
  // };

  const isSessionEnded = (date: string, duration: number): boolean => {
    const sessionEndTime = new Date(date).getTime() + duration * 60 * 1000;
    return Date.now() > sessionEndTime;
  };

  const [chatVisible, setChatVisible] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState("");
  const [messageInput, setMessageInput] = useState("");

  const [selectedId, setSelectedId] = useState("");
  const { mutateAsync: sendMessage, isLoading: isSendingMessage } =
    useSendConversationsMutation({
      id: selectedId,
      setMessageInput,
    });

  const { mutateAsync } = useMarkAsCompletedMutation();

  const handleChatClick = (_id: string) => {
    // if (!isChatEnabled(record.date)) {
    //   message.info("It'll be available 1 hour to the session");
    // } else {
    setSelectedId(_id);
    setChatVisible(true);
    // }
  };

  const handleMarkAsCompleted = async (id: string) => {
    await mutateAsync({ id: id });
  };

  const handleSendMessage = () => {
    sendMessage({ message: messageInput, sender: "Student", type: "text" });
  };

  const formatTimeRange = (timeSlot: any) => {
    const [hour, minute] = timeSlot.split(":").map(Number);
    const nextHour = (hour + 1) % 24;

    const formatTime = (h: number, m: number) => {
      const period = h >= 12 ? "pm" : "am";
      const formattedHour = h % 12 === 0 ? 12 : h % 12;
      return `${formattedHour}:${m.toString().padStart(2, "0")}${period}`;
    };

    return `${formatTime(hour, minute)} - ${formatTime(nextHour, minute)}`;
  };

  return (
    <div className="relative p-6 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div
        className={clsx(
          "absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold",
          STATUS_COLORS[status] || STATUS_COLORS.default
        )}
      >
        {status === "confirmed" && (
          <span className="h-2 w-2 bg-green-500 rounded-full animate-ping absolute" />
        )}
        <span className="relative">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      <h3 className="text-lg W2STMedium capitalize mt-[32px] mb-6">
        Session with{" "}
        <span className="text-primary700 font-semibold">
          {tutor?.firstName}
        </span>
      </h3>

      <div className="flex flex-col gap-4 mt-5">
        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <BookOpen
            color="currentColor"
            size={20}
            className="group-hover:text-primary700"
          />
          <span className="text-gray-700 capitalize group-hover:text-primary700">
            {subject?.subject} - {subject?.category}
          </span>
        </span>

        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <Calendar
            color="currentColor"
            size={20}
            className="group-hover:text-primary700"
          />
          <span className="text-gray-700 group-hover:text-primary700">
            {new Date(date).toLocaleDateString("en-US", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </span>

        <span className="flex gap-2.5 items-center group  transition-colors cursor-default">
          <Link color="currentColor" size={20} className="underline" />
          <a
            href={subject?.studentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-700 capitalize group-hover:text-primary700"
          >
            {subject?.studentUrl}
          </a>
        </span>

        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <Clock2
            color="currentColor"
            size={20}
            className="group-hover:text-primary700"
          />
          <div className="flex items-center gap-2 text-gray-700 group-hover:text-primary700">
            <span>
              <Moment format={"h:mm a"}>{startUtc}</Moment> -{" "}
              <Moment format={"h:mm a"}>{endUtc}</Moment>
            </span>
            <span className="text-sm text-gray-500">
              (
              <Moment fromNow interval={0}>
                {date}
              </Moment>
              )
            </span>
          </div>
        </span>
      </div>

      <div className="mt-6">
        <AvailabilityBadge availabilityType={tutorShipType} />
      </div>

      <Divider className="my-6" />

      <div className="flex flex-wrap gap-3 mt-4">
        <Button
          onClick={() => handleMarkAsCompleted(_id)}
          type="primary"
          size="large"
          disabled={!isSessionEnded(date, duration) || status === "completed"}
          className="flex-1 min-w-[140px] hover:opacity-90 transition-opacity"
        >
          Mark As Completed
        </Button>
        <Button
          type="primary"
          size="large"
          icon={<MessageOutlined />}
          disabled={status !== "confirmed"}
          onClick={() => {
            handleChatClick(_id);
            setSelectedTutor(tutor?.firstName);
          }}
          className="flex-1 min-w-[140px] hover:opacity-90 transition-opacity"
        >
          Chat Tutor
        </Button>
      </div>

      <Drawer
        title={
          <div className="flex items-center gap-2">
            <span className="font-medium">Chat with {tutor?.firstName}</span>
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          </div>
        }
        placement="right"
        width={window.innerWidth > 768 ? 500 : "100%"}
        onClose={() => {
          setChatVisible(false);
          setSelectedTutor("");
        }}
        open={chatVisible}
      >
        <div className="flex flex-col h-[calc(100vh-60px)]">
          <div className="flex-1 overflow-y-auto px-1">
            <ChatBox id={selectedId} />
          </div>
          <div className="sticky bottom-0 bg-white pt-4 pb-6 border-t border-gray-100">
            <div className="relative">
              <textarea
                value={messageInput}
                rows={4}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type your message here..."
                disabled={isSendingMessage}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                className="w-full min-h-[80px] max-h-[160px] p-4 pr-[100px] rounded-xl border border-gray-200 focus:border-primary700 focus:ring-1 focus:ring-primary700 resize-y placeholder:text-gray-400"
              />
              <Button
                type="primary"
                size="large"
                onClick={handleSendMessage}
                disabled={isSendingMessage || !messageInput.trim()}
                className="absolute bottom-4 right-4 hover:opacity-90 transition-opacity rounded-lg flex items-center gap-2"
              >
                Send
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default SessionCard;
